/**
 * TeamCard — shared aspect. Click-through card for the program
 * dashboard's right column. Two states: empty (no team yet) and
 * active (team name + captain pill + member avatars + chat link +
 * optional standing chip).
 *
 * State discrimination: members.length === 0 -> empty; > 0 -> active.
 *
 * Avatars are rendered via the optional renderAvatar callback so the
 * consumer can hand in its own Avatar component. If omitted, a
 * default initials-in-sand circle renders.
 */
import {
  Platform,
  Pressable,
  Text,
  View,
  type ViewStyle,
} from "react-native";
import Svg, { Path } from "react-native-svg";

import { COLORS, RADII, SPACING, STROKES } from "../../lib/brand";
import type { PaneTheme } from "../brand/paneThemes";
import { AvatarCluster } from "./AvatarCluster";

export type TeamCardMember = {
  id: string;
  name: string;
};

export type TeamCardStanding = {
  /** "STANDING" / "FINAL" — uppercase eyebrow. */
  label: string;
  /** "#4 of 12 · 81.2 pts" / "Not ranked yet" / "Eliminated at stage 2" */
  value: string;
  /** Drives the inline text color + font of the value. */
  tone: "amber" | "muted";
};

export type TeamCardProps = {
  theme: PaneTheme;
  members: TeamCardMember[];

  // --- Empty state ---
  emptyTitle?: string;
  emptySubtitle?: string;

  // --- Active state ---
  teamName?: string;
  isCaptain?: boolean;
  maxTeamSize?: number;
  standing?: TeamCardStanding | null;
  renderAvatar?: (member: TeamCardMember) => React.ReactNode;

  // --- Both ---
  ctaLabel: string;
  onPress: () => void;
  accessibilityLabel?: string;
};

export function TeamCard(props: TeamCardProps) {
  const {
    theme,
    members,
    ctaLabel,
    onPress,
    accessibilityLabel,
  } = props;
  const isWeb = Platform.OS === "web";
  const isActive = members.length > 0;

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? ctaLabel}
      style={({ hovered }: { hovered?: boolean }) => [
        {
          flex: 1,
          backgroundColor: theme.surfaceRaisedBg,
          borderRadius: RADII.lg,
          borderWidth: 0.5,
          borderColor:
            hovered && isWeb
              ? "rgba(28,43,26,0.32)"
              : "rgba(28,43,26,0.18)",
          padding: 18,
          minHeight: 168,
        },
        isWeb ? ({ cursor: "pointer" } as unknown as ViewStyle) : null,
      ]}
    >
      {isActive ? <ActiveBody {...props} /> : <EmptyBody {...props} />}
    </Pressable>
  );
}

function EmptyBody({
  theme,
  emptyTitle,
  emptySubtitle,
  ctaLabel,
}: TeamCardProps) {
  return (
    <View>
      <Text
        style={{
          fontFamily: theme.bodyBoldFont,
          fontSize: 10,
          letterSpacing: 1.4,
          color: theme.accent,
        }}
      >
        YOUR TEAM
      </Text>
      <Text
        style={{
          fontFamily: theme.displayMediumFont,
          fontSize: 20,
          color: theme.textOnSurface,
          marginTop: 6,
          lineHeight: 24,
        }}
        numberOfLines={1}
      >
        {emptyTitle ?? "Not on a team yet"}
      </Text>
      <Text
        style={{
          marginTop: 8,
          fontFamily: theme.bodyFont,
          fontSize: 12,
          color: theme.textOnSurfaceDim,
        }}
      >
        {emptySubtitle ?? "Find a public team or build your own."}
      </Text>
      <View
        style={{
          marginTop: 12,
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Text
          style={{
            fontFamily: theme.bodyBoldFont,
            fontSize: 11,
            letterSpacing: 1.2,
            color: theme.accent,
          }}
        >
          {ctaLabel}
        </Text>
        <Arrow theme={theme} />
      </View>
    </View>
  );
}

function ActiveBody({
  theme,
  members,
  teamName,
  isCaptain,
  maxTeamSize,
  standing,
  renderAvatar,
  ctaLabel,
}: TeamCardProps) {
  const memberCount = members.length;
  return (
    <View>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <Text
          numberOfLines={1}
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: theme.displayMediumFont,
            fontSize: 16,
            lineHeight: 20,
            color: theme.textOnSurface,
          }}
        >
          {teamName ?? ""}
        </Text>
        {isCaptain ? (
          <View
            style={{
              paddingHorizontal: SPACING.xs,
              paddingVertical: 2,
              borderRadius: RADII.full,
              backgroundColor: theme.accentTintSoft,
              borderWidth: STROKES.hairline,
              borderColor: theme.accentBorderSoft,
            }}
          >
            <Text
              style={{
                fontFamily: theme.bodyBoldFont,
                fontSize: 8.5,
                letterSpacing: 1.4,
                color: theme.accent,
                textTransform: "uppercase",
              }}
            >
              Captain
            </Text>
          </View>
        ) : null}
      </View>

      <View style={{ marginBottom: 14 }}>
        <AvatarCluster
          theme={theme}
          members={members}
          maxVisible={4}
          countMeta={
            maxTeamSize != null
              ? memberCount + " of " + maxTeamSize
              : undefined
          }
          renderAvatar={renderAvatar}
        />
      </View>

      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
        <Text
          style={{
            fontFamily: theme.bodyBoldFont,
            fontSize: 10.5,
            letterSpacing: 1.68,
            textTransform: "uppercase",
            color: theme.accentDeep,
          }}
        >
          {ctaLabel}
        </Text>
        <Arrow theme={theme} tone="deep" />
      </View>

      {standing ? (
        <View
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTopWidth: STROKES.hairline,
            borderTopColor: COLORS.SAND_LINE.hex,
            flexDirection: "row",
            alignItems: "baseline",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <Text
            style={{
              fontFamily: theme.bodyBoldFont,
              fontSize: 9.5,
              letterSpacing: 1.7,
              color:
                standing.tone === "amber"
                  ? theme.accentDeep
                  : theme.textOnSurfaceFaint,
              textTransform: "uppercase",
            }}
          >
            {standing.label}
          </Text>
          <Text
            style={{
              fontFamily:
                standing.tone === "amber"
                  ? theme.displayMediumFont
                  : theme.bodyMediumFont,
              fontSize: standing.tone === "amber" ? 15 : 12.5,
              color:
                standing.tone === "amber"
                  ? theme.textOnSurface
                  : theme.textOnSurfaceDim,
              fontVariant: ["tabular-nums"],
            }}
          >
            {standing.value}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

function Arrow({
  theme,
  tone,
}: {
  theme: PaneTheme;
  tone?: "deep";
}) {
  const color = tone === "deep" ? theme.accentDeep : theme.accent;
  return (
    <Svg width={10} height={10} viewBox="0 0 12 12">
      <Path
        d="M2 6 H10 M7 3 L10 6 L7 9"
        stroke={color}
        strokeWidth={1.6}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}
